import { useCallback, useEffect, useMemo, useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@jl/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { toastApiError } from "@/lib/toastApiError";
import { validateSignupPassword } from "@/lib/authApi";
import {
  assertLexiconEmailAvailable,
  createLexiconDirectoryUser,
  isLexiconEmailTaken,
  updateLexiconDirectoryUser,
  type LexiconDirectoryUser,
  type ManageableLexiconRole,
} from "@/lib/lexiconUsersApi";

export type AdminUserDialogMode = "create" | "edit";

const EMAIL_IN_USE_MESSAGE = "This email is already registered.";

type AdminUserDialogProps = {
  mode: AdminUserDialogMode;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user?: LexiconDirectoryUser | null;
  existingUsers?: LexiconDirectoryUser[];
  /** Receives the saved row (when the API echoes it) for an optimistic list update. */
  onSaved: (savedUser?: LexiconDirectoryUser | null) => void;
};

function splitDisplayName(name: string | null): { first: string; last: string } {
  if (!name?.trim()) {
    return { first: "", last: "" };
  }
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) {
    return { first: parts[0] ?? "", last: "" };
  }
  return { first: parts[0] ?? "", last: parts.slice(1).join(" ") };
}

const emptyForm = () => ({
  email: "",
  first_name: "",
  last_name: "",
  role: "contributor" as ManageableLexiconRole,
  password: "",
  confirmPassword: "",
  is_active: true,
});

export function AdminUserDialog({
  mode,
  open,
  onOpenChange,
  user,
  existingUsers = [],
  onSaved,
}: AdminUserDialogProps) {
  const [form, setForm] = useState(emptyForm);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);

  const emailExclude = useMemo(
    () =>
      mode === "edit" && user
        ? { user_id: user.user_id, id: user.id }
        : undefined,
    [mode, user]
  );

  useEffect(() => {
    if (!open) {
      setShowPassword(false);
      setShowConfirmPassword(false);
      setEmailError(null);
      setIsCheckingEmail(false);
      return;
    }
    if (mode === "edit" && user) {
      const { first, last } = splitDisplayName(user.display_name);
      const role: ManageableLexiconRole =
        user.lexicon_role === "admin"
          ? "admin"
          : user.lexicon_role === "scholar"
            ? "scholar"
            : "contributor";
      setForm({
        email: user.email ?? "",
        first_name: first,
        last_name: last,
        role,
        password: "",
        confirmPassword: "",
        is_active: user.is_active,
      });
      return;
    }
    setForm(emptyForm());
    setEmailError(null);
  }, [open, mode, user]);

  const validateEmailLocal = useCallback(
    (rawEmail: string): string | null => {
      const email = rawEmail.trim();
      if (!email) {
        return "Email is required.";
      }
      if (!email.includes("@")) {
        return "Enter a valid email address.";
      }
      if (isLexiconEmailTaken(email, existingUsers, emailExclude)) {
        return EMAIL_IN_USE_MESSAGE;
      }
      return null;
    },
    [existingUsers, emailExclude]
  );

  const validateEmailBeforeProceed = useCallback(
    async (rawEmail: string): Promise<boolean> => {
      const localErr = validateEmailLocal(rawEmail);
      if (localErr) {
        setEmailError(localErr);
        toastApiError(localErr);
        return false;
      }

      setIsCheckingEmail(true);
      const { available, error } = await assertLexiconEmailAvailable(
        rawEmail,
        emailExclude
      );
      setIsCheckingEmail(false);

      if (error) {
        toastApiError(error);
        return false;
      }
      if (!available) {
        setEmailError(EMAIL_IN_USE_MESSAGE);
        toastApiError(EMAIL_IN_USE_MESSAGE);
        return false;
      }

      setEmailError(null);
      return true;
    },
    [validateEmailLocal, emailExclude]
  );

  const handleEmailBlur = () => {
    const err = validateEmailLocal(form.email);
    setEmailError(err);
  };

  const emailInUse = emailError === EMAIL_IN_USE_MESSAGE;
  const formLocked = isSubmitting || isCheckingEmail || emailInUse;

  const handleSubmit = async () => {
    const email = form.email.trim();
    if (!email) {
      setEmailError("Email is required.");
      toastApiError("Email is required.");
      return;
    }

    const emailOk = await validateEmailBeforeProceed(email);
    if (!emailOk) {
      return;
    }

    if (!form.first_name.trim() || !form.last_name.trim()) {
      toastApiError("First and last name are required.");
      return;
    }

    if (mode === "create") {
      const pwErr = validateSignupPassword(form.password);
      if (pwErr) {
        toastApiError(pwErr);
        return;
      }
      if (form.password !== form.confirmPassword) {
        toastApiError("Passwords do not match.");
        return;
      }
    } else if (form.password.trim()) {
      const pwErr = validateSignupPassword(form.password);
      if (pwErr) {
        toastApiError(pwErr);
        return;
      }
      if (form.password !== form.confirmPassword) {
        toastApiError("Passwords do not match.");
        return;
      }
    }

    setIsSubmitting(true);
    try {
      let savedUser: LexiconDirectoryUser | null = null;
      if (mode === "create") {
        const { user: created, error } = await createLexiconDirectoryUser({
          email,
          first_name: form.first_name,
          last_name: form.last_name,
          role: form.role,
          password: form.password,
          password_confirm: form.confirmPassword,
          is_active: form.is_active,
        });
        if (error) {
          toastApiError(error);
          return;
        }
        savedUser = created;
        toast.success(
          created
            ? `${form.role === "admin" ? "Admin" : form.role === "scholar" ? "Scholar" : "Contributor"} created.`
            : "User created."
        );
      } else if (user) {
        const { user: updated, error } = await updateLexiconDirectoryUser(user, {
          email,
          first_name: form.first_name,
          last_name: form.last_name,
          role: form.role,
          is_active: form.is_active,
          ...(form.password.trim()
            ? {
                password: form.password,
                password_confirm: form.confirmPassword,
              }
            : {}),
        });
        if (error) {
          toastApiError(error);
          return;
        }
        savedUser = updated;
        toast.success("User updated.");
      }
      onOpenChange(false);
      onSaved(savedUser);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="z-[100] max-w-md sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {mode === "create" ? "Add user" : "Edit user"}
          </DialogTitle>
          <DialogDescription>
            {mode === "create"
              ? "Create an admin, scholar, or contributor account. Only admins can manage users."
              : "Update profile, role, password, or active status."}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-2">
          <div className="grid gap-2">
            <Label htmlFor="admin-user-email">Email</Label>
            <Input
              id="admin-user-email"
              type="email"
              autoComplete="email"
              value={form.email}
              disabled={isSubmitting || isCheckingEmail}
              onChange={e => {
                const next = e.target.value;
                setForm(f => ({ ...f, email: next }));
                setEmailError(validateEmailLocal(next));
              }}
              onBlur={() => handleEmailBlur()}
              className={cn(emailError && "border-destructive")}
              aria-invalid={!!emailError}
              aria-describedby={emailError ? "admin-user-email-error" : undefined}
            />
            {isCheckingEmail ? (
              <p className="text-muted-foreground text-xs">Checking email…</p>
            ) : null}
            {emailError ? (
              <p
                id="admin-user-email-error"
                className="text-destructive text-xs"
                role="alert"
              >
                {emailError}
              </p>
            ) : null}
          </div>
          <fieldset
            disabled={formLocked}
            className={cn(
              "grid min-w-0 gap-4 border-0 p-0",
              formLocked && "pointer-events-none opacity-60"
            )}
          >
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="grid gap-2">
              <Label htmlFor="admin-user-first">First name</Label>
              <Input
                id="admin-user-first"
                value={form.first_name}
                disabled={formLocked}
                onChange={e =>
                  setForm(f => ({ ...f, first_name: e.target.value }))
                }
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="admin-user-last">Last name</Label>
              <Input
                id="admin-user-last"
                value={form.last_name}
                disabled={formLocked}
                onChange={e =>
                  setForm(f => ({ ...f, last_name: e.target.value }))
                }
              />
            </div>
          </div>
          <div className="grid gap-2">
            <Label>Role</Label>
            <Select
              modal={false}
              value={form.role}
              disabled={formLocked}
              onValueChange={v =>
                setForm(f => ({ ...f, role: v as ManageableLexiconRole }))
              }
            >
              <SelectTrigger className="h-10 w-full" disabled={formLocked}>
                <SelectValue placeholder="Select role" />
              </SelectTrigger>
              <SelectContent
                position="popper"
                className="z-[110] max-h-60"
              >
                <SelectItem value="admin">Admin</SelectItem>
                <SelectItem value="scholar">Scholar</SelectItem>
                <SelectItem value="contributor">Contributor</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="admin-user-password">
              {mode === "create" ? "Password" : "New password (optional)"}
            </Label>
            <div className="relative">
              <Input
                id="admin-user-password"
                type={showPassword ? "text" : "password"}
                autoComplete={mode === "create" ? "new-password" : "off"}
                value={form.password}
                disabled={formLocked}
                onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                className="h-10 pr-10"
              />
              <button
                type="button"
                tabIndex={-1}
                disabled={formLocked}
                onClick={() => setShowPassword(v => !v)}
                className={cn(
                  "text-muted-foreground hover:text-foreground absolute right-1 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-md"
                )}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? (
                  <EyeOff className="h-4 w-4" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
              </button>
            </div>
          </div>
          {(mode === "create" || form.password.trim()) && (
            <div className="grid gap-2">
              <Label htmlFor="admin-user-confirm">Confirm password</Label>
              <div className="relative">
                <Input
                  id="admin-user-confirm"
                  type={showConfirmPassword ? "text" : "password"}
                  autoComplete="new-password"
                  value={form.confirmPassword}
                  disabled={formLocked}
                  onChange={e =>
                    setForm(f => ({ ...f, confirmPassword: e.target.value }))
                  }
                  className="h-10 pr-10"
                />
                <button
                  type="button"
                  tabIndex={-1}
                  disabled={formLocked}
                  onClick={() => setShowConfirmPassword(v => !v)}
                  className={cn(
                    "text-muted-foreground hover:text-foreground absolute right-1 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-md"
                  )}
                  aria-label={
                    showConfirmPassword
                      ? "Hide confirm password"
                      : "Show confirm password"
                  }
                >
                  {showConfirmPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
            </div>
          )}
          <div className="flex items-center justify-between gap-4 rounded-lg border border-border/60 px-3 py-3">
            <div>
              <Label htmlFor="admin-user-active" className="text-base">
                Active
              </Label>
              <p className="text-muted-foreground text-xs">
                Inactive users cannot sign in.
              </p>
            </div>
            <Switch
              id="admin-user-active"
              checked={form.is_active}
              disabled={formLocked}
              onCheckedChange={checked =>
                setForm(f => ({ ...f, is_active: checked }))
              }
            />
          </div>
          </fieldset>
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => void handleSubmit()}
            disabled={isSubmitting || isCheckingEmail || !!emailError}
          >
            {isSubmitting
              ? "Saving…"
              : isCheckingEmail
                ? "Checking email…"
                : mode === "create"
                  ? "Create user"
                  : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
